import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { Head } from 'react-static';
import './SiteNav.scss';
import { redirectWithCookies } from '../../utils/Utm';
import Picture from '../../components/Picture';
import { siteNavData } from './siteNavData';

const renderItemTitle = (title) => {
  if (typeof title === 'string') {
    return <h4>{title}</h4>;
  }
  return <h4>{title}</h4>;
};

const SiteNav = ({
  hideOnScroll = false,
  menuVisible = true,
  pixelsToShowMenuWhite = 50,
  transparentOnTop = false,
  whiteBackground = false,
  promoBarHeight = 0,
  loginOnly = false,
  logoOnly = false,
}) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [isAtTop, setIsAtTop] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSections, setMobileOpenSections] = useState({});
  const closeTimerRef = useRef(null);
  const dropdownRefs = useRef([]);

  const showTransparentTop = transparentOnTop && isAtTop && !whiteBackground && !mobileMenuOpen;

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setMobileOpenSections({});
  }, []);

  const toggleMobileMenu = () => {
    if (mobileMenuOpen) {
      closeMobileMenu();
    } else {
      setMobileMenuOpen(true);
    }
  };

  const toggleMobileSection = (index) => {
    setMobileOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const closeAllDropdowns = () => setActiveMenu(null);

  const handleNavItemEnter = (index) => {
    clearTimeout(closeTimerRef.current);
    setActiveMenu(index);

    const dd = dropdownRefs.current[index];
    if (!dd) return;

    dd.style.left = '';
    dd.style.right = '';

    requestAnimationFrame(() => {
      const rect = dd.getBoundingClientRect();
      const vw = window.innerWidth;
      if (rect.right > vw - 8) {
        const overflow = rect.right - (vw - 8);
        const currentLeft = parseFloat(window.getComputedStyle(dd).left) || 0;
        if (dd.style.right === '') {
          dd.style.left = `${currentLeft - overflow}px`;
        }
      }
      if (rect.left < 8) {
        dd.style.left = '0px';
      }
    });
  };

  const handleNavItemLeave = (index) => {
    closeTimerRef.current = setTimeout(() => {
      setActiveMenu((current) => (current === index ? null : current));
    }, 100);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsAtTop(scrollTop <= pixelsToShowMenuWhite);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pixelsToShowMenuWhite]);

  useEffect(() => {
    document.body.classList.toggle('mobile-menu-is-open', mobileMenuOpen);
    if (!mobileMenuOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('mobile-menu-is-open');
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!event.target.closest('.site-nav__item')) {
        closeAllDropdowns();
      }
    };

    const onEscape = (event) => {
      if (event.key === 'Escape') {
        closeAllDropdowns();
      }
    };

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onEscape);

    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onEscape);
      clearTimeout(closeTimerRef.current);
    };
  }, []);

  const navClassName = [
    'site-nav',
    hideOnScroll ? 'hide-on-scroll' : '',
    hideOnScroll && !menuVisible ? 'menu-hidden' : 'menu-visible',
    showTransparentTop ? 'at-top' : 'scrolled',
    promoBarHeight > 0 ? 'has-promo-bar' : '',
    mobileMenuOpen ? 'mobile-open' : '',
    logoOnly ? 'logo-only' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const menuItems = loginOnly ? [] : siteNavData;

  return (
    <Fragment>
      <Head>
        <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
      </Head>

      <header className={navClassName} id="siteHeader">
        <a href="/" className="site-nav__logo" aria-label="Mercos">
          <Picture
            src="/images/site/logo-mercos-colored.svg"
            alt="Mercos"
            className="site-nav__logo-img"
          />
        </a>

        {!loginOnly && !logoOnly && (
          <nav className="site-nav__desktop" aria-label="Navegação principal">
            <ul className="site-nav__menu">
              {menuItems.map((menuItem, idx) => (
                <li
                  key={menuItem.label}
                  className={`site-nav__item${activeMenu === idx ? ' open' : ''}`}
                  onMouseEnter={() => handleNavItemEnter(idx)}
                  onMouseLeave={() => handleNavItemLeave(idx)}
                >
                  <button
                    type="button"
                    className="site-nav__trigger"
                    aria-haspopup="true"
                    aria-expanded={activeMenu === idx}
                  >
                    {menuItem.label}
                  </button>
                  <div
                    ref={(el) => {
                      dropdownRefs.current[idx] = el;
                    }}
                    className={`site-nav__dropdown${menuItem.grid ? ' site-nav__dropdown--grid' : ''}`}
                    role="menu"
                  >
                    {menuItem.items.map((item, subIdx) => {
                      if (item.type === 'separator') {
                        return (
                          <div key={`${item.label}-${subIdx}`} className="site-nav__dd-label">
                            {item.label}
                          </div>
                        );
                      }

                      return (
                        <a
                          key={`${item.link}-${subIdx}`}
                          href={item.link}
                          className="site-nav__dd-item"
                          role="menuitem"
                          target={item.target}
                          rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                        >
                          <span className="material-icons site-nav__dd-icon">{item.icon}</span>
                          <div className="site-nav__dd-text">
                            {renderItemTitle(item.title)}
                            {item.description ? <p>{item.description}</p> : null}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {!logoOnly && (
        <div className="site-nav__actions">
          <button
            type="button"
            className="site-nav__cta"
            onClick={() => redirectWithCookies('https://app.mercos.com/cadastro/')}
          >
            comece grátis
          </button>
          <button
            type="button"
            className="site-nav__login"
            onClick={() => redirectWithCookies('https://app.mercos.com/login/')}
          >
            Fazer login <span className="material-icons">chevron_right</span>
          </button>
        </div>
        )}

        {!logoOnly && (
        <button
          type="button"
          className="site-nav__mobile-cta"
          onClick={() => redirectWithCookies('https://app.mercos.com/cadastro/')}
        >
          comece grátis
        </button>
        )}

        {!logoOnly && (
        <button
          type="button"
          className={`site-nav__hamburger${mobileMenuOpen ? ' active' : ''}`}
          id="headerHamburger"
          aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobileNav"
          onClick={toggleMobileMenu}
        >
          <span />
          <span />
          <span />
        </button>
        )}
      </header>

      {!logoOnly && (
      <nav
        className={`site-nav__mobile${mobileMenuOpen ? ' active' : ''}`}
        id="mobileNav"
        aria-label="Menu mobile"
        aria-hidden={!mobileMenuOpen}
      >
        {menuItems.map((menuItem, idx) => (
          <div
            key={menuItem.label}
            className={`site-nav__mob-section${mobileOpenSections[idx] ? ' open' : ''}`}
          >
            <button
              type="button"
              className="site-nav__mob-toggle"
              onClick={() => toggleMobileSection(idx)}
            >
              {menuItem.label}
              <span className="material-icons">expand_more</span>
            </button>
            <div className="site-nav__mob-items">
              <div className="site-nav__mob-items-inner">
                {menuItem.items.map((item, subIdx) => {
                  if (item.type === 'separator') {
                    return (
                      <div key={`${item.label}-${subIdx}`} className="site-nav__mob-label">
                        {item.label}
                      </div>
                    );
                  }

                  return (
                    <a
                      key={`${item.link}-${subIdx}`}
                      href={item.link}
                      className="site-nav__mob-item"
                      target={item.target}
                      rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                      onClick={closeMobileMenu}
                    >
                      <span className="material-icons">{item.icon}</span>
                      <div>
                        {renderItemTitle(item.title)}
                        {item.description ? <p>{item.description}</p> : null}
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        ))}

        <div className="site-nav__mob-actions">
          <button
            type="button"
            className="site-nav__mob-actions-cta"
            onClick={() => redirectWithCookies('https://app.mercos.com/cadastro/')}
          >
            comece grátis
          </button>
          <button
            type="button"
            className="site-nav__mob-actions-login"
            onClick={() => redirectWithCookies('https://app.mercos.com/login/')}
          >
            Fazer login <span className="material-icons">chevron_right</span>
          </button>
        </div>
      </nav>
      )}
    </Fragment>
  );
};

export default SiteNav;



// WEBPACK FOOTER //
// /usr/src/site-blog/src/blocks/Menu/SiteNav.jsx